export interface FormatDateOptions {
  locale?: string; // Locale code like "en-IN", "fr-FR"
  dateStyle?: "full" | "long" | "medium" | "short";
  timeStyle?: "full" | "long" | "medium" | "short";
  hour12?: boolean; // 12-hour or 24-hour format
  timeZone?: string; // Time zone identifier like "UTC", "America/New_York"
  // Allow any Intl.DateTimeFormatOptions to extend flexibility
  [key: string]: any;
}

/**
 * Formats a given date or timestamp to a locale-aware string.
 * Returns empty string on invalid input.
 *
 * @param input - Date | number (timestamp) | string (parsable date)
 * @param options - Formatting options with locale and styles
 * @returns Formatted date string
 */
export function formatDate(
  input: Date | number | string,
  options: FormatDateOptions = {}
): string {
  const {
    locale = "en-IN",
    dateStyle = "medium",
    timeStyle,
    hour12,
    timeZone,
    ...restOptions
  } = options;

  // Normalize input to Date
  let dateObj: Date;
  if (input instanceof Date) {
    dateObj = input;
  } else if (typeof input === "string" || typeof input === "number") {
    dateObj = new Date(input);
  } else {
    return "";
  }

  // Return empty if invalid date
  if (isNaN(dateObj.getTime())) {
    return "";
  }

  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      dateStyle,
      timeStyle,
      hour12,
      timeZone,
      ...restOptions,
    });

    return formatter.format(dateObj);
  } catch {
    // Fallback: ISO string or toLocaleString without options
    return dateObj.toLocaleString(locale);
  }
}

/**
 * Creates a reusable date formatter function preconfigured with locale and options.
 *
 * @param locale - Locale code like "en-IN"
 * @param options - FormatDateOptions excluding locale
 * @returns A function that formats dates with the preset configuration
 */
export function createDateFormatter(
  locale: string = "en-IN",
  options: Omit<FormatDateOptions, "locale"> = {}
) {
  const formatter = new Intl.DateTimeFormat(locale, options);

  return (input: Date | number | string): string => {
    const dateObj = input instanceof Date ? input : new Date(input);

    if (isNaN(dateObj.getTime())) {
      return "";
    }

    try {
      return formatter.format(dateObj);
    } catch {
      return dateObj.toLocaleString(locale);
    }
  };
}

// Example predefined formatter - medium date + time, 12-hour, US locale
export const indDateTimeFormatter = createDateFormatter("en-IN", {
  dateStyle: "medium",
  //   timeStyle: "short",
  hour12: true,
});

/**
 * Converts an ISO-8601 datetime into the "YYYY-MM-DDTHH:mm" shape that an
 * `<input type="datetime-local">` expects, in the browser's local time.
 * Returns "" for missing/invalid input so it can feed a form default directly.
 */
export function toDateTimeLocalValue(input?: string | Date | null): string {
  if (!input) return "";
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return "";

  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * Formats a date as a relative time string (e.g., "2 hours ago", "Yesterday").
 *
 * @param input - Date | number | string
 * @returns Relative time string
 */
export function formatTimeAgo(input: Date | number | string): string {
  const date = input instanceof Date ? input : new Date(input);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (isNaN(seconds)) return "";

  if (seconds < 60) return "Just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;

  // Fallback to formatted date if more than a week
  return formatDate(date, { dateStyle: "short" });
}
